import type { ReactNode } from 'react';
import { z } from 'zod';
import type { CompanyListItem } from '@/api/questionnaires/types';
import type { BulkActionDef } from '@/components/shared/DataTable/BulkActions';
import type {
  SortingState,
  ToolbarActionDef,
} from '@/components/shared/DataTable/types';

// ─── Re-exports from src/api/types for backwards compatibility ────────────────

export type {
  CompanyProfile,
  ProfileAuditEntry,
  ProfileHistoryResponse,
} from '@/api/companies/types';
export type {
  CompanySearchPage,
  CompanySearchResult,
  CreateGraphLinkPayload,
  DataPointSearchResult,
  EnrichedRiskFactor,
  EsgFieldType,
  EsgTemplateMetadata,
  GraphLayer,
  GraphLink,
  GraphProductionProcess,
  ImpactArea,
  IncompleteRequest,
  NetworkGraph,
  NetworkMapParams,
  PatchAnswerPayload,
  PatchGraphNodesPayload,
  PatchTemplatePayload,
  ProcessedRiskScores,
  ProfileTemplate,
  RiskFactor,
  RiskGroup,
  RiskMetricResult,
  TemplateId,
} from '@/api/network/types';
export type { GraphCompany, GraphNode } from '@/api/product-mapping/types';
export type {
  CompanyListItem,
  MyNetworkParams,
  MyNetworkResponse,
} from '@/api/questionnaires/types';
export type {
  AssessmentBlueprint,
  AssessmentBlueprintsResponse,
  RiskHistoryEntry,
  RiskLevelInfo as RiskLevel,
  RiskPrioritizationMeta,
  RiskPrioritizationResponse,
  RiskScores,
} from '@/api/risk/types';
export type { DataPoint, SlcpVerificationMeta } from '@/api/slcp/types';
export type { ConnectionType } from '@/api/supply-chain-mapping/types';

// ─── Route search schemas ─────────────────────────────────────────────────────

/** /network/my-network — the main table/map/graph view of the user's network */
export const networkListSearch = z.object({
  page: z.number().int().min(1).catch(1),
  limit: z.number().int().min(10).max(500).catch(50),
  sort: z
    .string()
    .transform(v => (/^[\w]+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  search: z.string().optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.array(z.string())),
    ])
    .optional(),
  view: z.enum(['table', 'map', 'graph']).catch('table'),
});

export type NetworkListSearch = z.infer<typeof networkListSearch>;

// ─── Field mapping ────────────────────────────────────────────────────────────

export type FieldMapping = {
  key: string;
  toApiValue: (raw: unknown, row?: CompanyListItem) => unknown;
  toLocalValue: (raw: unknown) => unknown;
};

// ─── Filter option types (kept here — route-local) ────────────────────────────

export interface FilterOption {
  key: string;
  label: string;
}

export interface CollaborationStatus {
  id: string;
  name: string;
}

export interface RelationshipType {
  id: string;
  name: string;
}

export interface FacilityProcessChild {
  key: string;
  text: string;
}

export interface FacilityProcessGroup {
  productionProcessId: string;
  productionProcessName: string;
  children: FacilityProcessChild[];
}

export interface DropdownUser {
  id: string;
  firstName: string;
  lastName: string;
}

export interface DropdownCompany {
  id: string;
  name: string;
  officialName?: string | null;
}

// ─── Component prop types ─────────────────────────────────────────────────────

export interface NetworkTableProps {
  data: CompanyListItem[];
  total: number;
  loading?: boolean;
  toolbarActions?: ToolbarActionDef[];
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (v: Record<string, boolean>) => void;
  sorting: SortingState;
  onSortingChange: (v: SortingState) => void;
  filters: Record<string, string[]>;
  onFiltersChange: (v: Record<string, string[]>) => void;
  pageIndex: number;
  pageSize: number;
  onPaginationChange: (v: { pageIndex: number; pageSize: number }) => void;
  onCellCommit: (
    rowIndex: number,
    colId: string,
    rawValue: unknown,
    row: CompanyListItem,
  ) => unknown;
  collaborationStatuses: FilterOption[];
  relationshipTypes: FilterOption[];
  facilityProcesses: FilterOption[];
  responsiblePersons: FilterOption[];
  networkCompanies: FilterOption[];
  bulkActions?: BulkActionDef[];
  overlay?: ReactNode;
  /**
   * Amfori last-synced timestamps keyed by companyId. When provided (i.e. the
   * brand has connected amfori credentials) an "Amfori synced" column is shown.
   */
  amforiLastSyncedByCompanyId?: Record<string, string | null>;
}
